import React from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material'
import {
  Close,
  Warning,
} from '@mui/icons-material'

interface ConfirmDialogProps {
  open: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmText?: string
  cancelText?: string
  severity?: 'warning' | 'error' | 'info'
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  severity = 'warning',
}) => {
  const getSeverityColor = () => {
    switch (severity) {
      case 'error':
        return 'error'
      case 'info':
        return 'info'
      default:
        return 'warning'
    }
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'surface',
          borderRadius: 4,
          border: '1px solid',
          borderColor: 'outlineVariant',
          boxShadow: 3,
          overflow: 'hidden',
        },
      }}
      sx={{
        '& .MuiBackdrop-root': {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(4px)',
        },
      }}
    >
      <DialogTitle
        sx={{
          backgroundColor: 'surfaceContainer',
          color: 'onSurface',
          py: 3,
          px: 4,
          borderBottom: '1px solid',
          borderColor: 'outlineVariant',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: 48,
                height: 48,
                borderRadius: 3,
                backgroundColor: `${getSeverityColor()}Container`,
                color: `on${getSeverityColor().charAt(0).toUpperCase() + getSeverityColor().slice(1)}Container`,
              }}
            >
              <Warning sx={{ fontSize: 24 }} />
            </Box>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 500, mb: 0.5, color: 'onSurface' }}>
                {title}
              </Typography>
              <Typography variant="caption" color="onSurfaceVariant" sx={{ textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                {severity} Action Required
              </Typography>
            </Box>
          </Box>
          <IconButton
            aria-label="close"
            onClick={onClose}
            sx={{
              color: 'onSurfaceVariant',
              '&:hover': {
                backgroundColor: 'surfaceVariant',
              },
            }}
          >
            <Close sx={{ fontSize: 20 }} />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ backgroundColor: 'surface', px: 4, py: 3 }}>
        <Box
          sx={{
            backgroundColor: 'surfaceContainer',
            borderRadius: 3,
            padding: 3,
            border: '1px solid',
            borderColor: 'outlineVariant',
          }}
        >
          <Typography
            variant="body1"
            color="onSurface"
            sx={{
              lineHeight: 1.6,
              fontSize: '1rem',
            }}
          >
            {message}
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          backgroundColor: 'surfaceContainer',
          borderTop: '1px solid',
          borderColor: 'outlineVariant',
          px: 4,
          py: 3,
          gap: 2
        }}
      >
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 7,
            borderColor: 'outline',
            color: 'onSurface',
            fontWeight: 500,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            '&:hover': {
              backgroundColor: 'surfaceVariant',
              borderColor: 'onSurface',
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={onConfirm}
          variant="contained"
          autoFocus
          sx={{
            backgroundColor: `${getSeverityColor()}.main`,
            color: `on${getSeverityColor().charAt(0).toUpperCase() + getSeverityColor().slice(1)}`,
            borderRadius: 7,
            fontWeight: 500,
            px: 4,
            py: 1.5,
            textTransform: 'none',
            fontSize: '1rem',
            boxShadow: 2,
            '&:hover': {
              backgroundColor: `${getSeverityColor()}.dark`,
              boxShadow: 4,
            },
            '&:active': {
              boxShadow: 1,
            },
            '&:focus': {
              boxShadow: `0 0 0 3px rgba(${getSeverityColor() === 'error' ? '245, 101, 101' : getSeverityColor() === 'warning' ? '237, 137, 54' : '66, 153, 225'}, 0.3)`,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
